import React, { useEffect, useRef, useState } from 'react';
import userActivityApi, { type Activity } from '../../services/userActivityApi';

interface ActivityFeedProps {
  activities?: Activity[];
  maxItems?: number;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ activities: propActivities, maxItems = 5 }) => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('activityCollapsed') === '1';
  });
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch real activities from API
  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      try {
        const fetchedActivities = await userActivityApi.getRecentActivities(maxItems);
        setActivities(fetchedActivities);
      } catch (error) {
        console.error('Error fetching activities:', error);
      } finally {
        setLoading(false);
      }
    };

    // Use prop activities if provided, otherwise fetch from API
    if (propActivities && propActivities.length > 0) {
      setActivities(propActivities.slice(0, maxItems));
      setLoading(false);
    } else {
      fetchActivities();
    }
  }, [propActivities, maxItems]);

  useEffect(() => {
    if (!contentRef.current) return;
    const measure = () => setMaxHeight(contentRef.current!.scrollHeight);
    requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activities, maxItems, loading]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('activityCollapsed', collapsed ? '1' : '0');
    }
  }, [collapsed]);
  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now.getTime() - time.getTime()) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="activity-feed">
        <div className="rooms-header">
          <h2 className="rooms-title">Recent Activity</h2>
          <div className="header-actions">
            <button
              className={`header-icon-btn chevron ${collapsed ? 'is-collapsed' : ''}`}
              aria-label={collapsed ? 'Expand activity' : 'Collapse activity'}
              aria-expanded={!collapsed}
              onClick={() => setCollapsed((c) => !c)}
            >
              ▼
            </button>
          </div>
        </div>
        {!collapsed && (
          <div className="activity-list">
            <div className="activity-item glass-card">
              <div className="activity-icon">⏳</div>
              <div className="activity-content">
                <p className="activity-message">Loading activities...</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="activity-feed">
      <div className="rooms-header">
        <h2 className="rooms-title">Recent Activity</h2>
        <div className="header-actions">
          <button
            className={`header-icon-btn chevron ${collapsed ? 'is-collapsed' : ''}`}
            aria-label={collapsed ? 'Expand activity' : 'Collapse activity'}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((c) => !c)}
          >
            ▼
          </button>
        </div>
      </div>
      <div
        ref={contentRef}
        className="collapsible"
        style={{ maxHeight: collapsed ? 0 : maxHeight }}
        aria-hidden={collapsed}
      >
      <div className="activity-list">
        {activities.length > 0 ? (
          activities.map((activity) => (
            <div key={activity.id} className="activity-item glass-card">
              <div className="activity-icon">{activity.icon}</div>
              <div className="activity-content">
                <p className="activity-message">{activity.message}</p>
                <span className="activity-time">{getTimeAgo(activity.timestamp)}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-activity activity-item glass-card">
            <div className="activity-icon">📭</div>
            <div className="activity-content">
              <p className="activity-message">No recent activity</p>
              <span className="activity-time">Join a game to see your activity here</span>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default ActivityFeed;
